// =============================================================================
// io/storage/loggerManifest.ts — read the published-build list.
// -----------------------------------------------------------------------------
// A plain `fetch` against a PUBLIC bucket object, not a supabase-js call: there
// is no auth involved and the manifest is world-readable by design, so routing
// it through the authenticated client would only add a token to a request that
// does not need one.
//
// ── 🚨 "NOT PUBLISHED YET" AND "COULD NOT REACH THE SERVER" ARE DIFFERENT ──
//
// The old client wraps the whole read in `catch (_) {}` and shows one message
// for both. They call for opposite actions: one is "wait for us", the other is
// "check your connection" — and a user staring at "downloads are being prepared"
// while actually offline will wait for something that already exists.
//
// So the outcome is a tagged result rather than a manifest-or-null.
// =============================================================================

import { parseManifest, type LoggerManifest } from '../../domain/loggerManifest';
import { reportFailure, reportFailureAs } from '../diagnostics/errorLog';

export type ManifestResult =
  /** Read and parsed. Individual platforms may still be null. */
  | { status: 'ok'; manifest: LoggerManifest }
  /** The bucket answered, and there is no manifest — nothing published yet. */
  | { status: 'unpublished' }
  /** The request never completed, or the body was not JSON. */
  | { status: 'unreachable'; error: string };

/**
 * @param fetchImpl injectable so a test can drive every branch without a
 *   network. Production always uses the global.
 */
export async function loadLoggerManifest(
  base: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ManifestResult> {
  let res: Response;
  try {
    // ⚠ `cache: 'no-store'`. The manifest is the thing that says what the latest
    // build IS, so a cached copy defeats the entire card — it would keep
    // offering last month's version after a release.
    res = await fetchImpl(`${String(base).replace(/\/+$/, '')}/manifest.json`, {
      cache: 'no-store',
    });
  } catch (e) {
    return { status: 'unreachable', error: reportFailure('logger manifest', e, 'network error') };
  }

  // ⚠ 404 is the PUBLISHED-NOTHING case and it is expected — storage returns it
  // for an object that does not exist. Any other bad status is a real fault.
  if (res.status === 404) return { status: 'unpublished' };
  // ⚠ Reported even though nothing was thrown. All three `unreachable` branches
  // print the same "Couldn't reach the download server" line, so logging two of
  // them would leave a hole at the likeliest fault of the three — a bad status
  // from storage.
  if (!res.ok) {
    return {
      status: 'unreachable',
      error: reportFailureAs('logger manifest', res, `server returned ${res.status}`),
    };
  }

  try {
    return { status: 'ok', manifest: parseManifest(await res.json()) };
  } catch (e) {
    // ⚠ A 200 carrying HTML — a proxy error page, a captive portal — reads as
    // success to `res.ok` and is not a manifest.
    //
    // ⚠ `reportFailureAs`: the sentence built here names WHICH read failed, and
    // a bare parse message ("Unexpected token <") on its own would not.
    return {
      status: 'unreachable',
      error: reportFailureAs(
        'logger manifest', e,
        e instanceof Error ? `the manifest could not be read (${e.message})` : 'bad manifest',
      ),
    };
  }
}
